import axios from "axios";
import { showAlert, handleRequestError } from "../../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import VMasker from "vanilla-masker";
import Swal from "sweetalert2";

const getLocalDateString = () => {
  const today = new Date();
  return (
    today.getFullYear() +
    "-" +
    String(today.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(today.getDate()).padStart(2, "0")
  );
};

export default () => ({
  // Main State
  currentDate: getLocalDateString(),
  appointmentsList: [],
  isModalOpen: false,
  isSubmitting: false,

  // Reschedule State
  isRescheduling: false,
  currentRescheduleId: null,

  // Filtros State
  searchQuery: "",
  statusFilter: "all",

  // Modal Dropdowns
  professionals: [],
  services: [],

  // Availability Engine
  availableSlots: [],
  isFetchingSlots: false,

  // Form State
  form: {
    user_id: "",
    service_id: "",
    date: getLocalDateString(),
    time: "",
    customer_name: "",
    customer_phone: "",
  },

  async initAppointments() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      this.initCalendars();

      this.$nextTick(() => {
        this.initPhoneMask();
      });

      await this.fetchAppointments();
      await this.fetchDependencies();

      // Reactivity for the availability engine
      this.$watch("form.user_id", () => this.checkAvailability());
      this.$watch("form.service_id", () => this.checkAvailability());
      this.$watch("form.date", () => this.checkAvailability());
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  initPhoneMask() {
    const phoneInput = document.getElementById(
      "appointment_customer_phone_input",
    );
    if (phoneInput) {
      VMasker(phoneInput).maskPattern("(99) 99999-9999");
    }
  },

  initCalendars() {
    flatpickr("#main-calendar", {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      defaultDate: this.currentDate,
      onChange: (selectedDates, dateStr) => {
        this.currentDate = dateStr;
        this.fetchAppointments();
      },
    });

    this.modalCalendarInstance = flatpickr("#modal-calendar", {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      defaultDate: this.form.date,
      onChange: (selectedDates, dateStr) => {
        this.form.date = dateStr;
      },
    });
  },

  async fetchAppointments() {
    try {
      const response = await axios.get(
        `/api/client/appointments?date=${this.currentDate}`,
      );
      this.appointmentsList = response.data.data;
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      console.error("Fetch Error:", msg);
    }
  },

  async fetchDependencies() {
    try {
      const [resUsers, resServices] = await Promise.all([
        axios.get("/api/client/appointments/professionals"),
        axios.get("/api/client/services"),
      ]);

      this.services = resServices.data.data.filter((s) => s.is_active);
      this.professionals = resUsers.data.data;
    } catch (error) {
      console.error("Error loading dependencies:", error);
    }
  },

  openCreateModal() {
    this.isRescheduling = false;
    this.currentRescheduleId = null;

    this.form = {
      user_id: "",
      service_id: "",
      date: this.currentDate,
      time: "",
      customer_name: "",
      customer_phone: "",
    };

    if (this.modalCalendarInstance) {
      this.modalCalendarInstance.setDate(this.currentDate);
    }

    this.availableSlots = [];
    this.isModalOpen = true;
  },

  /**
   * Opens the modal pre-filled with the selected appointment data for rescheduling.
   *
   * @param {Object} app
   */
  openRescheduleModal(app) {
    this.isRescheduling = true;
    this.currentRescheduleId = app.id;

    this.form = {
      user_id: app.user_id,
      service_id: app.service_id,
      date: this.currentDate, // Default to the currently viewed day
      time: "", // Forces the user to explicitly select a new time
      customer_name: app.customer_name,
      customer_phone: app.customer_phone,
    };

    if (this.modalCalendarInstance) {
      this.modalCalendarInstance.setDate(this.currentDate);
    }

    this.isModalOpen = true;

    // Explicitly check availability since we just set the ID's manually
    this.checkAvailability();
  },

  /**
   * Cancels an appointment with a confirmation prompt.
   *
   * @param {number} id
   */
  async cancelAppointment(id) {
    const confirm = await Swal.fire({
      title: "Cancelar Agendamento?",
      text: "O cliente será notificado sobre o cancelamento via WhatsApp.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444", // Red
      cancelButtonColor: "#94a3b8", // Slate
      confirmButtonText: "Sim, cancelar!",
      cancelButtonText: "Manter agendamento",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.patch(`/api/client/appointments/${id}/cancel`);
        showAlert("Agendamento cancelado com sucesso.", "success");
        await this.fetchAppointments();
      } catch (error) {
        handleRequestError(error, "Erro ao cancelar o agendamento.");
      }
    }
  },

  async checkAvailability() {
    this.form.time = "";
    this.availableSlots = [];

    if (!this.form.user_id || !this.form.service_id || !this.form.date) return;

    this.isFetchingSlots = true;
    try {
      const response = await axios.get(
        "/api/client/appointments/availability",
        {
          params: {
            user_id: this.form.user_id,
            service_id: this.form.service_id,
            date: this.form.date,
          },
        },
      );
      this.availableSlots = response.data.data;
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      this.isFetchingSlots = false;
    }
  },

  selectTime(time) {
    this.form.time = time;
  },

  /**
   * Submits the form either for creation or rescheduling based on the state.
   */
  async submitAppointment() {
    if (!this.form.time) {
      showAlert("Por favor, selecione um horário disponível.", "warning");
      return;
    }

    this.isSubmitting = true;
    try {
      if (this.isRescheduling) {
        // PATCH Request for Rescheduling
        await axios.patch(
          `/api/client/appointments/${this.currentRescheduleId}/reschedule`,
          this.form,
        );
        showAlert("Agendamento remarcado com sucesso!", "success");
      } else {
        // POST Request for Creation
        await axios.post("/api/client/appointments", this.form);
        showAlert("Agendamento confirmado!", "success");
      }

      if (this.form.date === this.currentDate) {
        await this.fetchAppointments();
      } else {
        // If they rescheduled to another day, redirect the view to that day
        this.currentDate = this.form.date;
        if (this.modalCalendarInstance) {
          document
            .querySelector("#main-calendar")
            ._flatpickr.setDate(this.form.date);
        }
        await this.fetchAppointments();
      }

      this.isModalOpen = false;
    } catch (error) {
      if (error.response && error.response.status === 409) {
        showAlert(error.response.data.error, "error");
        this.checkAvailability();
      } else {
        handleRequestError(
          error,
          this.isRescheduling
            ? "Erro ao remarcar."
            : "Erro ao criar agendamento.",
        );
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  /**
   * Computed property to filter appointments in memory
   * based on search query (name/phone) and status.
   */
  filteredAppointments() {
    return this.appointmentsList.filter((app) => {
      // 1. Status Check
      const matchesStatus =
        this.statusFilter === "all" || app.status === this.statusFilter;

      // 2. Search Check
      const searchLower = this.searchQuery.toLowerCase().trim();

      // Safe lowercase matching for name
      const nameMatches = (app.customer_name || "")
        .toLowerCase()
        .includes(searchLower);

      // Clean non-digits for phone matching
      const cleanSearchPhone = searchLower.replace(/\D/g, "");
      const cleanAppPhone = (app.customer_phone || "").replace(/\D/g, "");
      const phoneMatches =
        cleanSearchPhone !== "" && cleanAppPhone.includes(cleanSearchPhone);

      const matchesSearch = searchLower === "" || nameMatches || phoneMatches;

      return matchesStatus && matchesSearch;
    });
  },
});
